import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmptyState, PageHeading } from "@/components/states";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { expectedDeliveryDate, formatCurrency, formatDeliveryDate, formatNumber, getLocalizedField, isSupplierBackorder, isVatExempt } from "@/lib/product";
import { formatAddress, hasAddress } from "@/components/AddressFields";
import { useApiError } from "@/lib/useApiError";
import { useSite } from "@/context/SiteContext";
import { calcAmounts, makeReference, savePending } from "@/lib/payment";
import { cmsCall } from "@/lib/cms";
import { MinimumOrderAlert } from "@/components/MinimumOrderAlert";
import { ShippingSummary } from "@/components/ShippingSummary";
import { useMinimumOrder } from "@/hooks/useMinimumOrder";
import { useShipping } from "@/hooks/useShipping";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const Bestellen = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { lines, totalExcl, clear, blockedLines, hasBlockedLines } = useCart();
  const { bestellen, account } = useAuth();
  const navigate = useNavigate();
  const toMessage = useApiError();
  const { minimum, ontbreekt, gehaald } = useMinimumOrder();
  const verzending = useShipping();

  const { instellingen } = useSite();
  const bezorgadres = account?.organisatie?.bezorgadres || account?.organisatie?.adres || null;
  const factuuradres = account?.organisatie?.factuuradres || null;
  const zelfdeAdresregel =
    hasAddress(bezorgadres) && hasAddress(factuuradres) && formatAddress(bezorgadres) === formatAddress(factuuradres);
  const directBeschikbaar = Boolean(instellingen?.direct_betalen);
  const kortingPercentage = Number(instellingen?.korting_percentage || 0);
  const [methode, setMethode] = useState("factuur");
  const amounts = calcAmounts(lines, kortingPercentage, account);
  const [opmerking, setOpmerking] = useState("");
  const [referentie, setReferentie] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (lines.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="checkout-page">
        <PageHeading title={t("checkout.title")} testId="checkout-heading" />
        <div className="mt-6">
          <EmptyState
            title={t("errors.emptyCart")}
            actionLabel={t("cart.emptyAction")}
            onAction={() => navigate("/producten")}
          />
        </div>
      </div>
    );
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    // Niet-beschikbare producten mogen niet naar het CRM (briefing feb-2026 §2b).
    if (hasBlockedLines) {
      setError(t("cart.unavailableWarning", { count: blockedLines.length }));
      return;
    }
    if (!gehaald) {
      setError(t("cart.minimumBlocked"));
      return;
    }
    setSubmitting(true);
    const snapshot = lines.map((l) => ({
      product_id: l.product_id,
      aantal: Number(l.aantal),
      product: l.product,
    }));
    try {
      if (directBeschikbaar && methode === "direct") {
        const reference = makeReference();
        savePending({
          reference,
          lines: snapshot,
          opmerking: opmerking.trim(),
          referentie: referentie.trim(),
          amounts,
        });
        const res = await cmsCall("POST", "/betalingen", {
          reference,
          totaal_incl_btw: amounts.totaal_incl_btw,
          totaal_excl_btw: amounts.totaal_excl_btw,
          btw_bedrag: amounts.btw_bedrag,
          korting_bedrag: amounts.korting_bedrag,
          korting_percentage: amounts.korting_percentage,
        });
        window.location.assign(res.checkout_url);
        return;
      }
      const leverdatum = expectedDeliveryDate();
      const regels = snapshot.map((l) => ({
        product_id: l.product_id,
        aantal: l.aantal,
        ...(isSupplierBackorder(l.product) ? { verwachte_leverdatum: leverdatum.toISOString().slice(0, 10) } : {}),
      }));
      const leverancierSkus = snapshot.filter((l) => isSupplierBackorder(l.product)).map((l) => l.product?.sku).filter(Boolean);
      const orderOpmerking = [
        referentie.trim() ? t("checkout.referentieInOpmerking", { referentie: referentie.trim() }) : "",
        opmerking.trim(),
        leverancierSkus.length ? t("stock.supplierOrderNote", { datum: formatDeliveryDate(leverdatum, lang), skus: leverancierSkus.join(", ") }) : "",
      ].filter(Boolean).join("\n");
      const result = await bestellen(regels, orderOpmerking, undefined, referentie.trim() ? { klant_referentie: referentie.trim() } : {});
      clear();
      toast.success(t("confirmation.title"));
      navigate(`/besteld/${encodeURIComponent(result.ordernummer)}`, {
        replace: true,
        state: { result, lines: snapshot, opmerking: opmerking.trim(), referentie: referentie.trim() },
      });
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="checkout-page">
      <PageHeading
        title={t("checkout.title")}
        subtitle={t("checkout.subtitle")}
        testId="checkout-heading"
      />

      <form onSubmit={onSubmit} className="mt-6 grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <MinimumOrderAlert minimum={minimum} ontbreekt={ontbreekt} gehaald={gehaald} testId="checkout-minimum-alert" />
          {error && (
            <Alert variant="destructive" data-testid="checkout-error-alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {hasBlockedLines && (
            <Alert className="border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10" data-testid="checkout-unavailable-alert">
              <AlertDescription>
                {t("cart.unavailableWarning", { count: blockedLines.length })}{" "}
                <Link to="/mandje" className="underline" data-testid="checkout-fix-cart-link">
                  {t("checkout.backToCart")}
                </Link>
              </AlertDescription>
            </Alert>
          )}

          <Card className="p-6">
            <h2 className="font-display text-lg font-semibold">{t("checkout.summary")}</h2>
            <div className="mt-4 divide-y">
              {lines.map((line) => (
                <div
                  key={line.product_id}
                  className="flex items-start justify-between gap-4 py-3 first:pt-0"
                  data-testid="checkout-line"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {getLocalizedField(line.product, "naam", lang)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatNumber(line.aantal, lang)} ×{" "}
                      {formatCurrency(line.product?.verkoopprijs, lang)} {t("price.exclVat")}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatCurrency(
                      Number(line.product?.verkoopprijs || 0) * Number(line.aantal || 0),
                      lang,
                    )}
                  </p>
                </div>
              ))}
            </div>
          </Card>

          {directBeschikbaar && (
            <Card className="p-6" data-testid="payment-method-card">
              <h2 className="font-display text-lg font-semibold">{t("payment.title")}</h2>
              <RadioGroup value={methode} onValueChange={setMethode} className="mt-4 space-y-3">
                <Label className="flex cursor-pointer gap-3 rounded-md border p-3" htmlFor="pm-factuur">
                  <RadioGroupItem value="factuur" id="pm-factuur" data-testid="payment-option-invoice" />
                  <span>
                    <span className="block text-sm font-medium">{t("payment.invoice")}</span>
                    <span className="block text-xs text-muted-foreground">{t("payment.invoiceHint")}</span>
                  </span>
                </Label>
                <Label className="flex cursor-pointer gap-3 rounded-md border p-3" htmlFor="pm-direct">
                  <RadioGroupItem value="direct" id="pm-direct" data-testid="payment-option-direct" />
                  <span>
                    <span className="block text-sm font-medium">
                      {t("payment.direct", { korting: kortingPercentage })}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {t("payment.directHint", { korting: kortingPercentage })}
                    </span>
                  </span>
                </Label>
              </RadioGroup>

              {methode === "direct" && (
                <dl className="mt-4 space-y-1 border-t pt-4 text-sm" data-testid="payment-breakdown">
                  {[
                    [t("payment.subtotal"), amounts.subtotaal],
                    [t("payment.discount", { korting: kortingPercentage }), -amounts.korting_bedrag],
                    [t("payment.afterDiscount"), amounts.totaal_excl_btw],
                    [t("payment.vat"), amounts.btw_bedrag],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="tabular-nums">{formatCurrency(value, lang)}</dd>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4 border-t pt-2 font-semibold">
                    <dt>{t("cart.totalExcl")}</dt>
                    <dd className="tabular-nums" data-testid="payment-total-excl">
                      {formatCurrency(amounts.totaal_excl_btw, lang)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 text-xs text-muted-foreground">
                    <dt>{t("payment.totalIncl")}</dt>
                    <dd className="tabular-nums" data-testid="payment-total-incl">
                      {formatCurrency(amounts.totaal_incl_btw, lang)}
                    </dd>
                  </div>
                </dl>
              )}
            </Card>
          )}

          <Card className="p-6">
            <div className="space-y-2">
              <Label htmlFor="checkout-referentie">{t("checkout.referentie")}</Label>
              <Input
                id="checkout-referentie"
                value={referentie}
                maxLength={60}
                onChange={(e) => setReferentie(e.target.value)}
                placeholder={t("checkout.referentiePlaceholder")}
                data-testid="checkout-referentie-input"
              />
              <p className="text-xs text-muted-foreground">{t("checkout.referentieHint")}</p>
            </div>
            <div className="mt-5 space-y-2">
              <Label htmlFor="checkout-opmerking">{t("checkout.opmerking")}</Label>
              <Textarea
                id="checkout-opmerking"
                rows={4}
                value={opmerking}
                onChange={(e) => setOpmerking(e.target.value)}
                placeholder={t("checkout.opmerkingPlaceholder")}
                data-testid="checkout-opmerking-textarea"
              />
              <p className="text-xs text-muted-foreground">{t("checkout.opmerkingHint")}</p>
            </div>
          </Card>
        </div>

        <Card className="h-fit p-6 lg:col-span-5">
          {account && (
            <>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {t("checkout.deliverTo")}
              </p>
              <p className="mt-2 text-sm font-medium">{account.bedrijfsnaam}</p>
              <p className="text-sm text-muted-foreground">
                {[account.voornaam, account.achternaam].filter(Boolean).join(" ")}
              </p>
              <p className="text-sm text-muted-foreground">{account.email}</p>
              <Separator className="my-5" />
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {t("auth.register.address.shipping")}
              </p>
              {hasAddress(bezorgadres) ? (
                <p className="mt-2 text-sm" data-testid="checkout-shipping-address">
                  {formatAddress(bezorgadres)}
                </p>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground" data-testid="checkout-shipping-address-empty">
                  {t("checkout.addressUnknown")}{" "}
                  <Link to="/account" className="underline" data-testid="checkout-address-account-link">
                    {t("checkout.addressManage")}
                  </Link>
                </p>
              )}
              {hasAddress(factuuradres) && !zelfdeAdresregel && (
                <>
                  <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
                    {t("auth.register.address.billing")}
                  </p>
                  <p className="mt-2 text-sm" data-testid="checkout-billing-address">
                    {formatAddress(factuuradres)}
                  </p>
                </>
              )}
              <Separator className="my-5" />
            </>
          )}

          <div className="flex items-baseline justify-between">
            <span className="font-medium">{t("cart.totalExcl")}</span>
            <span className="text-xl font-semibold tabular-nums" data-testid="checkout-total">
              {formatCurrency(totalExcl, lang)}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="checkout-vat-notice">
            {isVatExempt(account) ? t("cart.vatNoticeExempt") : t("cart.vatNotice")}
          </p>
          <div className="mt-3">
            <ShippingSummary verzending={verzending} testId="checkout-shipping" />
          </div>
          <p className="mt-3 rounded-md border bg-secondary p-3 text-xs text-muted-foreground">
            {t("checkout.noPayment")}
          </p>

          <Button
            type="submit"
            className="mt-6 w-full whitespace-normal"
            disabled={submitting || hasBlockedLines || !gehaald}
            data-testid="checkout-submit-order-button"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {submitting
              ? t("checkout.submitting")
              : !gehaald
                ? t("cart.minimumBlocked")
                : directBeschikbaar && methode === "direct"
                  ? t("payment.payNow")
                  : t("checkout.submit")}
          </Button>
          <Button variant="ghost" className="mt-2 w-full" asChild data-testid="checkout-back-to-cart-button">
            <Link to="/mandje">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("checkout.backToCart")}
            </Link>
          </Button>
        </Card>
      </form>
    </div>
  );
};

export default Bestellen;
